"use client";

import { appConfig } from "@/constants";
import { analytics, getUser } from "@analytics";
import type { UserProfile } from "@imtbl/passport";
import {
  type LDMultiKindContext,
  LDProvider,
  useLDClient,
} from "launchdarkly-react-client-sdk";
import { type PropsWithChildren, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { usePassportProvider } from "./PassportProvider";

const buildContext = (
  segmentAnonId: string,
  userInfo: UserProfile | undefined,
  walletAddress: string | undefined,
): LDMultiKindContext => {
  return {
    kind: "multi",
    ...(segmentAnonId && {
      browser: { key: segmentAnonId },
    }),
    ...(userInfo && {
      user: {
        key: userInfo.sub,
        email: userInfo.email,
        wallet: walletAddress,
      },
    }),
  };
};

const InnerProvider = ({
  children,
  segmentAnonId,
}: PropsWithChildren<{ segmentAnonId: string | undefined }>) => {
  const ldClient = useLDClient();

  const { isLoggedIn, userInfo, walletAddress } = usePassportProvider();

  // Build LaunchDarkly context when all dependencies are ready
  // biome-ignore lint/correctness/useExhaustiveDependencies: Don't depend on ldClient to avoid double identifying on initial page load
  useEffect(() => {
    if (
      isLoggedIn === undefined ||
      segmentAnonId === undefined ||
      ldClient === undefined
    ) {
      return;
    }

    const multiContext = buildContext(segmentAnonId, userInfo, walletAddress);

    ldClient.identify(multiContext);
  }, [isLoggedIn, userInfo, walletAddress, segmentAnonId]);

  return children;
};

export const FeatureFlagProvider = ({ children }: PropsWithChildren) => {
  const { isLoggedIn, userInfo, walletAddress } = usePassportProvider();
  const [segmentAnonId, setSegmentAnonId] = useState<string | undefined>();
  const [initialContext, setInitialContext] = useState<
    LDMultiKindContext | undefined
  >();

  // Get Segment anonymous ID once on mount
  useEffect(() => {
    getUser(analytics)
      .then((user) => {
        const anonId = user.anonymousId();
        if (!anonId)
          throw new Error(`anonymousId() returned falsey value: ${anonId}`);
        setSegmentAnonId(anonId);
      })
      .catch((err) => {
        console.error(
          "unable to get anonymousId, a random id will be used instead",
          err,
        );
        setSegmentAnonId(uuidv4());
      });
  }, []);

  // Build LaunchDarkly context when all dependencies are ready
  useEffect(() => {
    // Only set the initial context if it's not already set because LDProvider does not sync context post initialization.
    // The useEffect in InnerProvider handles subsequent updates.
    if (
      isLoggedIn === undefined ||
      segmentAnonId === undefined ||
      initialContext !== undefined
    ) {
      return;
    }

    const multiContext = buildContext(segmentAnonId, userInfo, walletAddress);

    setInitialContext(multiContext);
  }, [isLoggedIn, userInfo, walletAddress, segmentAnonId, initialContext]);

  // Initialize LaunchDarkly with deferInitialization to prevent blocking
  return (
    <LDProvider
      clientSideID={appConfig.LAUNCH_DARKLY_CLIENT_ID}
      deferInitialization
      context={initialContext}
    >
      <InnerProvider segmentAnonId={segmentAnonId}>{children}</InnerProvider>
    </LDProvider>
  );
};
